import Dialog from "@mui/material/Dialog";
import { styled as muiStyled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import theme from "../../style/theme";
import { Confirm } from "../../types/confirm";
import useDidMountEffect from "../../utils/custom-hook/use-did-mount-effect";
import { ImageData, fileToImageData } from "../../utils/image/image-data";
import { LocationImageDropZone } from "../dropzone/LocationImageDropZone";
import { LocationImageDropZoneButton } from "../dropzone/LocationImageDropZoneButton";
import ArrowLeftRightLineIcon from "../icons/ArrowLeftRightLineIcon/ArrowLeftRightLineIcon";
import { DraggableSmallLocationImagePreview } from "../panel/DraggableSmallLocationImagePreview";
import { LocationImageSwiper } from "../swiper/LocationImageSwiper";
import { DialogFooter } from "./DialogFooter";
import { DialogTitle } from "./DialogTitle";

const BootstrapDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

interface Props {
  imageData: ImageData[];
  initImageData: () => void;
  setImageData: React.Dispatch<React.SetStateAction<ImageData[]>>;
  handleClickLocationImageDialogClose: () => void;
}

export const LocationImageFormDialog: React.FC<Props> = ({
  imageData,
  initImageData,
  setImageData,
  handleClickLocationImageDialogClose,
}) => {
  const [dialogImageData, setDialogImageData] =
    useState<ImageData[]>(imageData);
  const [selectedImage, setSelectedImage] = useState<ImageData>();

  const handleClickApplyButton = () => {
    setImageData(dialogImageData);
    handleClickLocationImageDialogClose();
    toast.success(Confirm.Confirm);
  };

  const setFiles = (files: File[]) => {
    const newImageData = files.map((file) => fileToImageData(file));
    setDialogImageData(
      dialogImageData.concat(
        newImageData.filter((data) => !dialogImageData.includes(data))
      )
    );
  };

  const handleClickInitButton = () => {
    initImageData();
  };

  useEffect(() => {
    setDialogImageData(imageData);
  }, [imageData]);

  useDidMountEffect(() => {
    if (dialogImageData.length > 0 && !selectedImage) {
      setSelectedImage(dialogImageData[0]);
    }
    if (dialogImageData.length === 0) {
      setSelectedImage(undefined);
    }
  }, [dialogImageData.length]);

  return (
    <BootstrapDialog
      onClose={handleClickLocationImageDialogClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      maxWidth={false}
    >
      <LocationImageFormDialogContainer>
        <DialogTitle
          title="사진 첨부하기"
          onClick={handleClickLocationImageDialogClose}
        />

        <LocationBodyContainer>
          {dialogImageData.length > 0 ? (
            <LocationImageSwiper
              imgItemsContents={dialogImageData.map(
                (imageData) => imageData.url
              )}
              selectedImage={selectedImage}
            />
          ) : (
            <LocationImageDropZone setFiles={setFiles} />
          )}
          <BodyContainer>
            <BodyLeftContainer>
              <BodyTitleContainer>
                <BodyTitleLeft>등록된 사진</BodyTitleLeft>
                <BodyTitleRight>{dialogImageData.length}장</BodyTitleRight>
              </BodyTitleContainer>
              <BodyContentContainer>
                <BodyContentLeft>
                  <ArrowLeftRightLineIcon
                    width="1.063rem"
                    height="1.063rem"
                    fill={theme.palette.primitives.gray300}
                  />
                </BodyContentLeft>
                <BodyContentRight>드래그로 순서 전환</BodyContentRight>
              </BodyContentContainer>
            </BodyLeftContainer>
            {dialogImageData.length > 0 && (
              <BodyRightContainer>
                <LocationImageDropZoneButton setFiles={setFiles} />
              </BodyRightContainer>
            )}
          </BodyContainer>
          {dialogImageData.length > 0 ? (
            <DraggableSmallLocationImagePreview
              dialogImageData={dialogImageData}
              setImageData={setDialogImageData}
              selectedImage={selectedImage}
              setSelectedImage={setSelectedImage}
            />
          ) : (
            <EmptyImagesContainer>
              <EmptyImagesContent>등록된 사진이 없습니다.</EmptyImagesContent>
            </EmptyImagesContainer>
          )}
        </LocationBodyContainer>
        <DialogFooter
          confirmButtonText={"첨부완료"}
          onClickInitButton={handleClickInitButton}
          onClickConfirmButton={handleClickApplyButton}
        />
      </LocationImageFormDialogContainer>
    </BootstrapDialog>
  );
};

const LocationImageFormDialogContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TitleContentContainer = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  line-height: 1.5rem;
  color: ${theme.palette.primitives.black};
`;

const CloseSmallIconContainer = styled.div`
  cursor: pointer;
`;

const BodyTitleContainer = styled.div`
  display: flex;
  font-style: normal;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.188rem;
  color: ${theme.palette.primitives.black};
`;

const BodyTitleLeft = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.188rem;
  color: ${theme.palette.primitives.black};
`;

const BodyTitleRight = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.188rem;
  color: ${theme.palette.primitives.gray400};
  margin-left: 0.3rem;
`;

const EmptyImageContainer = styled.div`
  display: flex;
  width: 100%;
  height: 23rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid ${theme.palette.primitives.gray200};
  border-radius: 0.25rem;
  margin-top: 2rem;
`;

const EmptyImageText = styled.div`
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.188rem;
  color: ${theme.palette.primitives.gray500};
`;

const DialogBottomContainer = styled.div`
  display: fixed;
  justify-content: flex-end;
  align-items: center;
  bottom: 0;
  height: 91px;
`;

const CreateButtonContainer = styled.div`
  margin-left: 0.5rem;
`;

const AddImageButtonContainer = styled.div`
  margin: 1.75rem 0 1rem 0;
`;

const BodyContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2.25rem;
`;

const BodyLeftContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const BodyRightContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const BodyContentContainer = styled.div`
  display: flex;
  margin: 1rem 0;
`;

const BodyContentLeft = styled.div``;

const BodyContentRight = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.063rem;
  color: ${theme.palette.primitives.gray400};
  margin-left: 0.25rem;
`;

const EmptyImagesContainer = styled.div`
  height: 7.063rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${theme.palette.primitives.gray50};
  border-radius: 0.25rem;
`;

const EmptyImagesContent = styled.div`
  font-weight: 500;
  font-size: 1.125rem;
  line-height: 1.375rem;
  color: ${theme.palette.primitives.gray400};
`;

const LocationBodyContainer = styled.div`
  height: 550px;
  overflow-y: scroll;
  padding: 0 40px 16px;
  width: 33.375rem;
`;
